import React from 'react';
import { CheckCircle2Icon, CircleIcon, MinusCircleIcon } from 'lucide-react';
import { Field, FormData } from '../types';
import { resolveIcon } from '../utils/icons';
export interface SectionProgress {
  id: string;
  title: string;
  icon: string;
  fields: Field[];
  hiddenCount: number;
}
interface SectionChecklistProps {
  sections: SectionProgress[];
  values: FormData;
  dirtyIds: Set<string>;
  onSelect: (sectionId: string) => void;
}
function isFilled(field: Field, values: FormData): boolean {
  const value = values[field.id];
  if (field.type === 'checkbox') return true;
  return String(value ?? '').trim() !== '';
}
export function computeSectionStats(section: SectionProgress, values: FormData) {
  const total = section.fields.length;
  const filled = section.fields.filter(f => isFilled(f, values)).length;
  const empty = section.fields.filter(f => !isFilled(f, values));
  const complete = total > 0 && filled === total;
  return {
    total,
    filled,
    empty,
    complete
  };
}
export function SectionChecklist({
  sections,
  values,
  dirtyIds,
  onSelect
}: SectionChecklistProps) {
  const stats = sections.map(s => ({
    section: s,
    ...computeSectionStats(s, values)
  }));
  const completeCount = stats.filter(s => s.complete).length;
  const totalFields = stats.reduce((sum, s) => sum + s.total, 0);
  const filledFields = stats.reduce((sum, s) => sum + s.filled, 0);
  return <aside aria-label="Section completion" className="lg:sticky lg:top-44 lg:self-start" data-id="element-301" data-mp="1dsxhlg1b3816">
      <div className="mb-3" data-id="element-302" data-mp="190vwd4m5u6z0">
        <h2 className="text-sm font-bold uppercase tracking-wide text-slate-900" data-id="element-303" data-mp="18zkvpdo0hfc4">
          Record Completion
        </h2>
        <p className="text-xs text-slate-500" data-id="element-304" data-mp="13zzrootzigsj">
          {completeCount} of {sections.length} sections complete · {filledFields}/{totalFields}{' '}
          required fields
        </p>
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-200" data-id="element-305" data-mp="v0l4by946mc9">
          <div className="h-full rounded-full bg-emerald-500 transition-all duration-300" style={{
          width: `${totalFields ? filledFields / totalFields * 100 : 0}%`
        }} data-id="element-306" data-mp="q2tfxe7ohict" />
        </div>
      </div>

      <ul className="space-y-2" data-id="element-307" data-mp="kdy09nu086cp">
        {stats.map(({
        section,
        total,
        filled,
        empty,
        complete
      }) => {
        const Icon = resolveIcon(section.icon);
        const started = filled > 0;
        const editedCount = section.fields.filter(f => dirtyIds.has(f.id)).length;
        const pct = total ? filled / total * 100 : 0;
        return <li key={section.id} data-id="element-308" data-mp="142yotawyye3j">
              <button type="button" onClick={() => onSelect(section.id)} aria-label={`Go to ${section.title} — ${filled} of ${total} fields complete`} className={`w-full rounded-xl border px-3 py-2.5 text-left transition-colors ${complete ? 'border-emerald-200 bg-emerald-50/60 hover:bg-emerald-50' : started ? 'border-blue-200 bg-white hover:bg-blue-50/50' : 'border-slate-200 bg-white hover:bg-slate-50'}`} data-id="element-309" data-mp="1462z8q11262n">
                <div className="flex items-start gap-2" data-id="element-310" data-mp="3gdgxktfy6qvr">
                  <span className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md ${complete ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`} data-id="element-311" data-mp="3gcysr8f7z4c8">
                    <Icon className="h-3.5 w-3.5" data-id="element-312" data-mp="2jnzk3saq40ji" />
                  </span>

                  <span className="min-w-0 flex-1" data-id="element-313" data-mp="2xmqv35uah63f">
                    <span className="block truncate text-sm font-semibold text-slate-800" data-id="element-314" data-mp="2xok1ck0zpoaw">
                      {section.title}
                    </span>
                    <span className="mt-0.5 block truncate text-xs font-medium text-slate-600" data-id="element-315" data-mp="cw8i2g8p0347">
                      {complete ? `All ${total} required fields filled` : `${empty.length} required field${empty.length === 1 ? '' : 's'} empty${section.hiddenCount ? ` · ${section.hiddenCount} hidden` : ''}`}
                    </span>
                  </span>

                  <span className="shrink-0 pt-0.5" data-id="element-316" data-mp="1d846jnmovood">
                    {complete ? <CheckCircle2Icon className="h-5 w-5 text-emerald-600" data-id="element-317" data-mp="1i6ff7v18zt50" /> : started ? <MinusCircleIcon className="h-5 w-5 text-blue-500" data-id="element-318" data-mp="1m4v8ggnx9xqv" /> : <CircleIcon className="h-5 w-5 text-slate-300" data-id="element-319" data-mp="1r3fzpql75736" />}
                  </span>
                </div>

                <div className="mt-2 flex items-center gap-2" data-id="element-320" data-mp="20vvfqicxnllf">
                  <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-200" data-id="element-321" data-mp="20xge6j2dbook">
                    <div className={`h-full rounded-full transition-all duration-300 ${complete ? 'bg-emerald-500' : 'bg-blue-500'}`} style={{
                  width: `${pct}%`
                }} data-id="element-322" data-mp="25svm6udp2qd2" />
                  </div>
                  <span className="shrink-0 text-xs font-bold tabular-nums text-slate-600" data-id="element-323" data-mp="3qf3sdjow1s3x">
                    {filled}/{total}
                  </span>
                </div>

                {!complete && empty.length > 0 && <div className="mt-2" data-id="element-324" data-mp="hxfoljvtccfm">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500" data-id="element-325" data-mp="hweepiqj9vzh">
                      Next up
                    </p>
                    <ul className="mt-1 space-y-1" data-id="element-326" data-mp="86upzwg537wp">
                      {empty.slice(0, 3).map(f => <li key={f.id} className="flex items-center gap-1.5 text-xs font-semibold text-slate-700" data-id="element-327" data-mp="38jg5lpugxeh">
                          <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-blue-500" aria-hidden="true" data-id="element-328" data-mp="vw70czn7dm0l" />
                          <span className="truncate" data-id="element-329" data-mp="2u5wd1t08evnl">{f.label}</span>
                        </li>)}
                    </ul>
                    {empty.length > 3 && <p className="mt-1 text-xs font-medium text-slate-500" data-id="element-330" data-mp="2lducluq5xfdb">
                        +{empty.length - 3} more
                      </p>}
                  </div>}

                {editedCount > 0 && <span className="mt-2 inline-block rounded bg-amber-100 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider text-amber-700" data-id="element-331" data-mp="2bh7uhdp812ez">
                    {editedCount} unsaved
                  </span>}
              </button>
            </li>;
      })}
      </ul>
    </aside>;
}